/**
 * ChatTimeline Component
 * Displays message history and streaming content
 */

import React, { useRef, useEffect, useState } from 'react';
import { TimelineItem } from '../types';
import { User, Bot, ArrowDown, AlertCircle } from 'lucide-react';
import { MarkdownRenderer } from './MarkdownRenderer';
import { StreamingIndicator } from './StreamingIndicator';
import { ToolExecutionCard } from './ToolExecutionCard';

interface ChatTimelineProps {
  timeline: TimelineItem[];
  streamingMessage: string | null;
  isStreaming: boolean;
  error: string | null;
}

export const ChatTimeline: React.FC<ChatTimelineProps> = ({
  timeline,
  streamingMessage,
  isStreaming,
  error,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [unseenMessageCount, setUnseenMessageCount] = useState(0);
  const lastSeenMessageCountRef = useRef(0);

  // Get reference to scroll container (parent element)
  useEffect(() => {
    // The scroll container is the parent with overflow-y-auto
    const findScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;
      const parent = element.parentElement;
      if (!parent) return null;
      const overflowY = window.getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return parent;
      }
      return findScrollContainer(parent);
    };
    
    if (bottomRef.current) {
      scrollContainerRef.current = findScrollContainer(bottomRef.current);
    }
  }, []);

  // Detect if user has scrolled up from bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Consider "near bottom" if within 100px of bottom
      const isNearBottom = distanceFromBottom < 100;
      const wasScrolledUp = isUserScrolledUp;
      const newScrolledUpState = !isNearBottom;
      setIsUserScrolledUp(newScrolledUpState);

      console.log('[ChatTimeline] Scroll state:', {
        isNearBottom,
        isUserScrolledUp: newScrolledUpState,
        distanceFromBottom,
        unseenMessageCount,
        lastSeenCount: lastSeenMessageCountRef.current,
        currentMessageCount: timeline.length
      });

      // If user JUST scrolled up, record the current message count as "last seen"
      if (!wasScrolledUp && newScrolledUpState) {
        console.log('[ChatTimeline] User just scrolled up, setting last seen to:', timeline.length);
        lastSeenMessageCountRef.current = timeline.length;
        setUnseenMessageCount(0);
      }

      // If user scrolled back to bottom, reset unseen count
      if (wasScrolledUp && isNearBottom) {
        console.log('[ChatTimeline] User scrolled to bottom, resetting count');
        setUnseenMessageCount(0);
        lastSeenMessageCountRef.current = timeline.length;
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isUserScrolledUp, timeline.length, unseenMessageCount]);

  // Track unseen messages when user is scrolled up
  useEffect(() => {
    if (isUserScrolledUp) {
      // Count new messages since user scrolled up
      const newMessageCount = timeline.length - lastSeenMessageCountRef.current;
      if (newMessageCount > 0) {
        console.log('[ChatTimeline] User scrolled up, unseen messages:', newMessageCount);
        setUnseenMessageCount(newMessageCount);
      }
    } else {
      // User is at bottom, update last seen count
      lastSeenMessageCountRef.current = timeline.length;
      setUnseenMessageCount(0);
    }
  }, [timeline.length, isUserScrolledUp]);

  // Auto-scroll to bottom only if user hasn't scrolled up
  // Use instant scroll during streaming to be less aggressive
  useEffect(() => {
    if (!isUserScrolledUp) {
      // Use instant scroll to avoid fighting with user's scroll attempts
      bottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [timeline, streamingMessage, isUserScrolledUp]);

  // DON'T reset scroll state when streaming stops - let user control scrolling
  // Only reset when they explicitly scroll to bottom (handled in handleScroll)

  // Handle scroll to bottom button click
  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsUserScrolledUp(false);
    setUnseenMessageCount(0);
    lastSeenMessageCountRef.current = timeline.length;
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Empty state */}
      {timeline.length === 0 && !streamingMessage && (
        <div className="flex flex-col items-center justify-center h-full text-center py-12">
          <Bot className="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Welcome to Pipeline Assistant
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            I can help you build, modify, and optimize your pipeline.
            Ask me anything about adding processors, connecting steps, or configuring your workflow.
          </p>
        </div>
      )}

      {/* Timeline items - both messages and tool executions */}
      {timeline.map((item) => {
        if (item.itemType === 'message') {
          return (
            <div
              key={item.id}
              className={`flex gap-3 ${
                item.role === 'user' ? 'flex-row-reverse' : 'flex-row'
              }`}
            >
              {/* Avatar */}
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  item.role === 'user'
                    ? 'bg-blue-100 dark:bg-blue-900/30'
                    : 'bg-gray-100 dark:bg-gray-800'
                }`}
              >
                {item.role === 'user' ? (
                  <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <Bot className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                )}
              </div>

              {/* Message Content Container */}
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                {/* Message Bubble */}
                <div
                  className={`flex ${
                    item.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-2 ${
                      item.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white'
                    }`}
                  >
                    <MarkdownRenderer content={item.content} />
                  </div>
                </div>

                {/* Timestamp */}
                <div
                  className={`px-1 text-xs text-gray-500 dark:text-gray-400 ${
                    item.role === 'user' ? 'text-right' : 'text-left'
                  }`}
                >
                  {item.timestamp.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          );
        } else if (item.itemType === 'tool') {
          return (
            <div key={item.id} className="flex gap-3 flex-row">
              {/* Spacer to align with assistant messages */}
              <div className="flex-shrink-0 w-8" />
              
              {/* Tool execution card */}
              <div className="flex-1 min-w-0 max-w-[85%]">
                <ToolExecutionCard execution={item} />
              </div>
            </div>
          );
        }
        return null;
      })}

      {/* Streaming Message */}
      {(streamingMessage || isStreaming) && (
        <div className="flex gap-3 flex-row">
          {/* Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <Bot className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </div>

          {/* Streaming Content Container */}
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            {streamingMessage ? (
              // Show text content (no dots during active streaming)
              <div className="max-w-[85%] rounded-lg px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white">
                <MarkdownRenderer content={streamingMessage} />
              </div>
            ) : (
              // Show only dots when waiting for response (no content yet)
              <div className="rounded-lg px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white">
                <StreamingIndicator />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex gap-3 flex-row">
          {/* Avatar */}
          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/20">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          </div>

          {/* Error Content */}
          <div className="flex-1 min-w-0 flex justify-start">
            <div className="max-w-[85%] rounded-lg px-4 py-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900 dark:text-red-200 mb-1">
                    Agent Error
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to bottom button (floating) */}
      {isUserScrolledUp && unseenMessageCount > 0 && (
        <div 
          className="sticky bottom-4 left-0 right-0 flex justify-center pointer-events-none z-50 mt-4"
          style={{ marginTop: '-4rem' }}
        >
          <button
            onClick={handleScrollToBottom}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-colors pointer-events-auto animate-bounce"
            data-testid="scroll-to-bottom"
          >
            <ArrowDown className="h-4 w-4" />
            <span className="text-sm font-medium">
              {unseenMessageCount} new message{unseenMessageCount !== 1 ? 's' : ''}
            </span>
          </button>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </div>
  );
};
